import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Globe2, RefreshCw } from 'lucide-react';
import GrowthTabNav from './GrowthTabNav';
import GrowthDateFilter from './GrowthDateFilter';
import { getGrowthSources } from '../../Services/GrowthService';
import { GrowthSource } from '../../Types/GrowthType';

const headerClass =
  'px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500';

function GrowthSourcesComponent() {
  const [data, setData] = useState<GrowthSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getGrowthSources({
        fromDate: appliedFrom || undefined,
        toDate: appliedTo || undefined,
      });
      if (response.success && Array.isArray(response.data)) {
        setData(response.data);
      }
    } catch (err) {
      console.error('Failed to load growth sources', err);
    } finally {
      setLoading(false);
    }
  }, [appliedFrom, appliedTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const maxVisitors = useMemo(() => Math.max(1, ...data.map((s) => s.visitors)), [data]);

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <GrowthTabNav />

      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
          <Globe2 size={22} />
        </span>
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Acquisition Sources</h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            Visitors, registrations &amp; approvals grouped by UTM source
          </p>
        </div>
        <button
          onClick={fetchData}
          title="Refresh"
          className="ml-auto inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-3 xl:grid-cols-2">
        <GrowthDateFilter
          fromDate={fromDate}
          toDate={toDate}
          onApply={(f, t) => {
            setFromDate(f);
            setToDate(t);
            setAppliedFrom(f);
            setAppliedTo(t);
          }}
        />
      </div>

      {loading && data.length === 0 ? (
        <div className="flex min-h-[40vh] items-center justify-center text-slate-400">
          <RefreshCw className="h-10 w-10 animate-spin text-cyan-500" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
          <table className="w-full min-w-[820px] text-left">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-900/60">
                <th className={headerClass}>Source</th>
                <th className={headerClass}>Visitors</th>
                <th className={headerClass}>Registration Started</th>
                <th className={headerClass}>Registered</th>
                <th className={headerClass}>Approved</th>
                <th className={headerClass}>Conversion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {data.map((source, idx) => (
                <motion.tr
                  key={`${source.source}-${idx}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  className="transition hover:bg-slate-900/70"
                >
                  <td className="px-5 py-4">
                    <span className="text-sm font-bold text-slate-100">{source.source || 'Unknown'}</span>
                    {source.medium && (
                      <span className="mt-0.5 block text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                        {source.medium}
                      </span>
                    )}
                    <div className="mt-1.5 h-1.5 w-36 overflow-hidden rounded-full bg-slate-800">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-teal-500"
                        style={{ width: `${Math.max(2, (source.visitors / maxVisitors) * 100)}%` }}
                      />
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm font-semibold text-slate-200">{source.visitors.toLocaleString()}</td>
                  <td className="px-5 py-4 text-sm font-semibold text-slate-300">{source.registrationStarted.toLocaleString()}</td>
                  <td className="px-5 py-4 text-sm font-semibold text-slate-300">{source.registered.toLocaleString()}</td>
                  <td className="px-5 py-4">
                    <span className="inline-flex rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-bold text-emerald-400">
                      {source.approved.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm font-black text-cyan-400">{source.conversionPct.toFixed(1)}%</td>
                </motion.tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-16 text-center text-sm font-semibold text-slate-500">
                    No source data for this range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default GrowthSourcesComponent;